/**
 * Club repeated daily / weekly cron tasks into one list row.
 *
 * The task list renders one table per status.  Callers pass a single
 * status bucket into ``clubRoutineRows``, so a completed copy of a
 * routine joins the completed section and never the assigned or
 * in-progress club.
 *
 * The newest occurrence in that section stays on its own row.  Only
 * the older copies are collapsed behind the + control, and only when
 * there are at least two of them.
 *
 * The API stamps ``routine_club_key`` (see
 * ``task_management.routine_grouping``).  When that field is present
 * and null, the task stays on its own row — including monthly and
 * annual routines whose date window can look daily or weekly.  Payloads
 * from an older API omit the field; those fall back to the cron's
 * date window (same day = daily, six days later = weekly).
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function normalizeTitle(title) {
  return String(title || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function utcMidnight(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? '').trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const ms = Date.UTC(year, month - 1, day);
  const check = new Date(ms);
  if (
    check.getUTCFullYear() !== year
    || check.getUTCMonth() !== month - 1
    || check.getUTCDate() !== day
  ) {
    return null;
  }
  return ms;
}

/** ``daily`` or ``weekly`` when the span matches a cron occurrence window. */
export function inferCadence(task) {
  if (!task || String(task.routine || '').trim() !== 'routine') return null;
  const start = utcMidnight(task.start_date);
  const end = utcMidnight(task.end_date);
  if (start == null || end == null) return null;
  const span = Math.round((end - start) / 86400000);
  if (span === 0) return 'daily';
  if (span === 6) return 'weekly';
  return null;
}

/**
 * Stable identity of one daily/weekly cron routine, or null when this
 * task must stay on its own row.
 */
export function routineClubKey(task) {
  if (!task || typeof task !== 'object') return null;
  if (Object.prototype.hasOwnProperty.call(task, 'routine_club_key')) {
    const key = task.routine_club_key;
    if (key == null || String(key).trim() === '') return null;
    return String(key);
  }
  const cadence = inferCadence(task);
  if (!cadence) return null;
  const creator = task.created_by == null ? '' : String(task.created_by);
  return `legacy:${creator}:${cadence}:${normalizeTitle(task.title)}`;
}

export function clubCadence(tasks) {
  for (const task of tasks || []) {
    const explicit = String(task?.routine_frequency || '').trim().toLowerCase();
    if (explicit === 'daily' || explicit === 'weekly') return explicit;
  }
  for (const task of tasks || []) {
    const inferred = inferCadence(task);
    if (inferred) return inferred;
  }
  return 'daily';
}

export function clubMetaLabel(cadence, count) {
  const weekly = cadence === 'weekly';
  const name = weekly ? 'Weekly' : 'Daily';
  const noun = weekly
    ? (count === 1 ? 'earlier week' : 'earlier weeks')
    : (count === 1 ? 'earlier day' : 'earlier days');
  return `${name} · ${count} ${noun}`;
}

function occurrenceStamp(task) {
  const start = utcMidnight(task?.start_date);
  if (start != null) return start;
  return utcMidnight(task?.end_date);
}

/** Newest occurrence in a routine group. Same day breaks on the higher id. */
export function latestRoutineTask(tasks) {
  let best = null;
  let bestStamp = null;
  let bestId = -Infinity;
  for (const task of tasks || []) {
    const stamp = occurrenceStamp(task);
    const id = Number(task?.id);
    const numericId = Number.isFinite(id) ? id : -Infinity;
    if (!best) {
      best = task;
      bestStamp = stamp;
      bestId = numericId;
      continue;
    }
    const newer = stamp != null && (bestStamp == null || stamp > bestStamp);
    const sameDay = stamp === bestStamp;
    if (newer || (sameDay && numericId > bestId)) {
      best = task;
      bestStamp = stamp;
      bestId = numericId;
    }
  }
  return best;
}

export function formatOccurrenceDate(value) {
  const ms = utcMidnight(value);
  if (ms == null) return '';
  const date = new Date(ms);
  return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/** Human label for one expanded day or week. */
export function occurrenceCaption(task, cadence) {
  const start = formatOccurrenceDate(task?.start_date);
  const end = formatOccurrenceDate(task?.end_date);
  if (cadence === 'weekly' && start && end && task.start_date !== task.end_date) {
    return `${start} – ${end}`;
  }
  return start || end || '';
}

/** Due-date cell for a club: one date, or the earliest–latest span. */
export function clubEndLabel(tasks) {
  const ends = (tasks || [])
    .map((task) => String(task?.end_date || '').trim())
    .filter(Boolean)
    .sort();
  if (ends.length === 0) return '—';
  if (ends[0] === ends[ends.length - 1]) return ends[0];
  return `${ends[0]} – ${ends[ends.length - 1]}`;
}

export function unionAssignees(tasks) {
  const seen = new Set();
  const people = [];
  for (const task of tasks || []) {
    for (const person of task?.assignees || []) {
      const id = person?.id ?? person?.email ?? person?.full_name;
      if (id == null || seen.has(id)) continue;
      seen.add(id);
      people.push(person);
    }
  }
  return people;
}

/** Shared field value, or null when the club's tasks disagree. */
export function uniformValue(tasks, getValue) {
  const values = (tasks || []).map((task) => {
    const value = getValue(task);
    return value == null ? '' : String(value);
  });
  if (values.length === 0) return '';
  const first = values[0];
  return values.every((value) => value === first) ? first : null;
}

function groupKey(task) {
  const base = routineClubKey(task);
  if (!base) return null;
  return `${task.status || ''}\u0000${base}`;
}

/**
 * Keep the newest daily/weekly occurrence on its own row and collapse
 * the older copies.
 *
 * The newest row stays where that task already sat in ``tasks`` (the
 * list's current sort).  The club of older copies is inserted where the
 * first older copy sat.  One older copy is not a club — a + needs at
 * least two earlier days or weeks.  Different statuses never share a
 * club, even when this function is handed a mixed list.
 */
export function clubRoutineRows(tasks) {
  const list = Array.isArray(tasks) ? tasks : [];
  const indexesByKey = new Map();
  list.forEach((task, index) => {
    const key = groupKey(task);
    if (!key) return;
    const indexes = indexesByKey.get(key);
    if (indexes) indexes.push(index);
    else indexesByKey.set(key, [index]);
  });

  const latestIndex = new Map();
  const clubKeys = new Set();
  for (const [key, indexes] of indexesByKey) {
    if (indexes.length < 2) continue;
    const members = indexes.map((index) => list[index]);
    const latest = latestRoutineTask(members);
    const at = indexes.find((index) => list[index] === latest);
    latestIndex.set(key, at);
    if (indexes.length - 1 >= 2) clubKeys.add(key);
  }

  const emitted = new Set();
  const rows = [];
  list.forEach((task, index) => {
    const key = groupKey(task);
    if (!key || !latestIndex.has(key) || index === latestIndex.get(key) || !clubKeys.has(key)) {
      rows.push({ kind: 'task', task });
      return;
    }
    if (emitted.has(key)) return;
    emitted.add(key);
    const older = indexesByKey.get(key)
      .filter((member) => member !== latestIndex.get(key))
      .map((member) => list[member]);
    rows.push({
      kind: 'club',
      key,
      tasks: older,
      cadence: clubCadence(older),
    });
  });
  return rows;
}
